import React from 'react'
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import tableStyles from '../../styles/gri/styledReportTable.module.css'

/**
 * Styled Report Table Component for GRI Reports with custom column backgrounds
 * @param {Array} columns - Array of column objects with { field: string, label: string, width?: string }
 * @param {Array} rows - Array of row data objects
 * @param {string} title - Optional title above the table
 * @param {boolean} showSerialNumber - Whether to show S.No. column (default: true)
 */
const StyledReportTable = ({ 
  columns, 
  rows = [], 
  title = null,
  showSerialNumber = true 
}) => {
  // Add S.No. column if showSerialNumber is true
  const tableColumns = showSerialNumber 
    ? [{ field: 'sno', label: 'S.No.', width: '60px' }, ...columns]
    : columns

  return (
    <Box className={tableStyles.wrapper}>
      {title && (
        <Typography
          className={tableStyles.title}
        >
          {title}
        </Typography>
      )}

      <TableContainer 
        component={Paper} 
        className={tableStyles.tableContainer}
      >
        <Table className={tableStyles.table}>
          <TableHead>
            <TableRow className={tableStyles.headerRow}>
              {tableColumns.map((column, index) => (
                <TableCell
                  key={index}
                  className={tableStyles.headerCell}
                  style={{ width: column.width || 'auto' }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows && rows.length > 0 ? (
              rows.map((row, rowIndex) => {
                const isTotal = row.isTotal || false
                
                return (
                  <TableRow 
                    key={rowIndex}
                    className={tableStyles.bodyRow}
                  >
                    {isTotal && showSerialNumber ? (
                      // Total row: Merge S.No. and Economic Value Generated columns into one cell
                      <>
                        <TableCell 
                          colSpan={tableColumns.length - 1}
                          className={tableStyles.totalCell}
                          style={{ backgroundColor: '#F2F6F8' }}
                        >
                          Total
                        </TableCell>
                        <TableCell 
                          className={tableStyles.totalCell}
                          style={{ backgroundColor: '#F2F6F8' }}
                        >
                          {row[columns[columns.length - 1]?.field] || ''}
                        </TableCell>
                      </>
                    ) : (
                      // Regular rows: Render all columns normally
                      tableColumns.map((column, colIndex) => {
                        const cellContent = column.field === 'sno' 
                          ? `${rowIndex + 1}.`
                          : row[column.field] || '-'

                        return (
                          <TableCell 
                            key={colIndex}
                            className={tableStyles.cell}
                            style={{
                              textAlign: column.field === 'sno' ? 'center' : 'left',
                              backgroundColor: '#F2F6F8'
                            }}
                          >
                            {cellContent}
                          </TableCell>
                        )
                      })
                    )}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell 
                  colSpan={tableColumns.length}
                  className={tableStyles.emptyCell}
                  style={{ backgroundColor: '#F2F6F8' }}
                >
                  No information provided.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default StyledReportTable
